import * as globals from '@wandb/weave/common/css/globals.styles';
import {Button} from '@wandb/weave/components/Button';
import {Icon, IconClose} from '@wandb/weave/components/Icon';
import {Input, Modal as SemanticModal, Popup} from 'semantic-ui-react';
import styled, {css, keyframes} from 'styled-components';

import CopyableText from '../CopyableText';
import {ShareReportModalHeaderAction} from './action';

export const CloseIcon = styled(IconClose)`
  z-index: 1;
  position: absolute;
  top: 24px;
  right: 32px;

  cursor: pointer;

  color: ${globals.THEME.textTertiary};
  :hover {
    color: ${globals.THEME.accentHover};
  }
`;

export const Modal = styled(SemanticModal)`
  border-radius: 8px !important;
  width: 560px !important;
  min-width: 375px !important;
  top: 8vw;

  @media (max-width: 560px) {
    width: 100% !important;
  }

  max-height: '590px';
`;

export const ModalContent = styled(Modal.Content)`
  &&& {
    border-radius: 0.75rem !important;
    padding: 24px 32px 0px !important;
  }
`;

export const ScrollContent = styled.div`
  overflow-y: hidden;
  overflow-x: hidden;
  padding-top: 24px;

  @media (max-width: 560px) {
    overflow-y: auto;
  }

  max-height: 452px;

  padding-bottom: 24px;
`;

export const EmailScrollContent = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 24px;

  max-height: 452px;

  padding-bottom: 24px;
`;

export const ShareInputSection = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: 420px) {
    flex-wrap: wrap !important;
    width: 100%;
    justify-content: flex-end;
  }
`;

export const TextInputLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  cursor: default;
  margin: ${globals.spu};
  color: ${globals.THEME.textPrimary};
`;

interface InputProps {
  error?: boolean;
  disabled?: boolean;
  focused?: boolean;
}
const getTextInputBorderColor = (props: InputProps) => {
  const {error, disabled, focused} = props;
  if (error) {
    return globals.THEME.statusErrorBorder;
  }
  if (disabled) {
    return globals.THEME.border;
  }
  if (focused) {
    return globals.THEME.accent;
  }
  return globals.THEME.border;
};
export const TextInput = styled(Input)<InputProps>`
  &&& {
    width: 100%;
    margin-right: ${globals.spu};
    border: 1px solid ${props => getTextInputBorderColor(props)};
    box-sizing: border-box;
    border-radius: 5px;
    outline: none !important;
    input {
      border: none;
      outline: none;
      box-shadow: none;
    }
    .label {
      border: none;
      outline: none;
      box-shadow: none;
      font-weight: 400;
      font-size: 15px;
      color: ${props => (props.disabled ? globals.THEME.textTertiary : globals.THEME.textPrimary)};
    }

    @media (max-width: ${globals.MOBILE_WIDTH}px) {
      width: 80%;
    }

    @media (max-width: 420px) {
      width: 100%;
      margin: 0 0 4px 0;
    }

    :focus {
    }
  }
`;

export const SimpleSmallTextWrapper = styled.div`
  font-size: 14px;
  line-height: 15px;
  color: ${globals.THEME.textPrimary};
  font-weight: 400;
`;

export const ControlsWrapper = styled.div`
  margin: 12px 0 24px;
`;

export const RowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 16px;
  color: ${globals.THEME.textPrimary};
`;

export const IconTextWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 75%;

  @media (max-width: ${globals.MOBILE_WIDTH}px) {
    width: 65%;
  }
`;

export const GrayCircleWBIcon = styled(Icon)`
  &&& {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    width: 32px;
    color: white;
    background-color: ${globals.THEME.border};
    border-radius: 50%;
    margin-right: ${globals.spu};
    font-size: 20px;
  }
`;

export const EmailText = styled.span`
  width: calc(100% - 32px); // Account for circle icon
  text-overflow: ellipsis;
  overflow: hidden;
`;

const growthPulse = keyframes`
  0% {
      font-size: 24px;
  }
  35% {
      font-size: 28px;
  }
  65% {
      font-size: 28px;
  }
  100% {
      font-size: 24px;
  }
`;
export const CopyableLink = styled(CopyableText)<{isAnimated?: boolean}>`
  background: ${globals.THEME.bgSurfaceRaised} !important;
  border: 1px solid ${globals.THEME.border} !important;
  border-sizing: border-box !important;
  border-radius: 5px !important;
  i::before {
    color: ${globals.THEME.textLink};
  }

  color: ${globals.THEME.textPrimary};
  background: ${globals.THEME.bgSurfaceSunken};
  width: auto;
  max-width: unset;
  margin: 12px 0 0;
  padding: 0.5em 0.75em;

  i {
    opacity: 1 !important;
    height: 24px;
    width: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    ${({isAnimated}) =>
      isAnimated &&
      css`
        animation: ${growthPulse} 1s;
      `}
  }

  .copyable-text-content {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  :hover {
    color: ${globals.THEME.textPrimary};
    .copy-icon {
      color: ${globals.THEME.textPrimary} !important;
    }
  }
`;

// TODO - fix font size
export const ShareOptionsWrapper = styled.div`
  > i {
    margin-right: 0;
    font-size: 20px;
    height: 16px;
    width: 16px;
    vertical-align: top;
    margin-top: 1px;
  }

  cursor: pointer;
`;

export const DropdownTrigger = styled.span<{disabled?: boolean}>`
  display: flex;
  justify-content: flex-end;

  align-items: center;
  margin-right: -4px;
  font-weight: 600;
  font-size: 16px;
  gap: 8px;
  color: ${props => (props.disabled ? globals.THEME.textSecondary : globals.THEME.textPrimary)};
  :hover {
    cursor: pointer;
    color: ${globals.THEME.accentHover};
  }
  width: 88px;
`;

export const Tooltip = styled(Popup)<{hidden?: boolean}>`
  &&& {
    display: ${props => (props.hidden ? 'none' : 'block')} !important;
    background-color: ${globals.THEME.bgInverse} !important;
    white-space: pre !important;
    ::before {
      background-color: ${globals.THEME.bgInverse} !important;
      display: ${props => (props.hidden ? 'none' : 'block')} !important;
    }
    color: ${globals.THEME.textInverse} !important;
  }
`;

export const ShareButton = styled(Button)<{disabled?: boolean}>`
  @media (max-width: 420px) {
    width: 100%;
  }
  display: flex;
  justify-content: center;
`;
